import { getPaletteShadeHex } from './color-palette-resolve.util';
import { isParamRefValue, readParamRefKeyFromValue } from './resolve-bound-value';

export function resolveParamRefTarget(
  values: Record<string, unknown>,
  refKey: string,
  catalog: Record<string, { type?: string }> = {},
): unknown {
  const k = refKey.trim();
  if (!k) return undefined;

  if (k in values) {
    let v = values[k];
    let guard = 0;
    while (isParamRefValue(v) && guard++ < 32) {
      const next = readParamRefKeyFromValue(v);
      if (!next) break;
      v = resolveParamRefTarget(values, next, catalog);
    }
    return v;
  }

  const path = splitParamRefPath(k);
  if (!path) return undefined;
  const { parentKey, suffix } = path;
  if (!(parentKey in values)) return undefined;

  let parentVal = values[parentKey];
  let guard = 0;
  while (isParamRefValue(parentVal) && guard++ < 32) {
    const next = readParamRefKeyFromValue(parentVal);
    if (!next) break;
    parentVal = resolveParamRefTarget(values, next, catalog);
  }

  const parentType = catalog[parentKey]?.type;
  if (parentType === 'color_palette' || parentType === 'colors') {
    return getPaletteShadeHex(parentVal, suffix);
  }

  if (parentVal != null && typeof parentVal === 'object' && !Array.isArray(parentVal)) {
    return (parentVal as Record<string, unknown>)[suffix];
  }
  return undefined;
}

export function resolveParamValuesMap(
  raw: Record<string, unknown>,
  catalog: Record<string, { type?: string }> = {},
  maxDepth = 32,
): Record<string, unknown> {
  const out = { ...raw };
  const visiting = new Set<string>();

  const resolveOne = (key: string, depth: number): unknown => {
    if (depth > maxDepth) return out[key];
    const val = out[key];
    if (!isParamRefValue(val)) return val;
    const targetKey = readParamRefKeyFromValue(val);
    if (!targetKey || targetKey === key) return val;
    if (visiting.has(key)) return val;
    visiting.add(key);
    const resolved = resolveParamRefTarget(out, targetKey, catalog);
    visiting.delete(key);
    return resolved;
  };

  for (const key of Object.keys(out)) {
    out[key] = resolveOne(key, 0);
  }
  return out;
}

function splitParamRefPath(refKey: string): { parentKey: string; suffix: string } | null {
  const k = refKey.trim();
  const lastDot = k.lastIndexOf('.');
  if (lastDot <= 0) return null;
  const parentKey = k.slice(0, lastDot);
  const suffix = k.slice(lastDot + 1);
  if (!parentKey || !suffix) return null;
  return { parentKey, suffix };
}
